<template>
    <div class="container-fluid">
        <div class="loading" v-if="loading">Loading&#8230;</div>
        <div class="view-header my-4">
            <div class="view-header__main">
                <div class="view-header__info">
                    <h1 class="view-header__title">Inventario</h1>
                    <p class="view-header__subtitle">Listado de productos del inventario.</p>
                </div>
                <div class="view-header__actions">
                    <v-guard :permissions="['CREAR_PRODUCTOS']">
                        <button
                            type="button"
                            class="btn view-header__btn view-header__btn--primary"
                            @click="nuevoProductoLink()"
                        >
                            <i class="bi bi-plus-lg"></i>
                            Nuevo producto
                        </button>
                    </v-guard>
                </div>
            </div>
        </div>
        <!-- FILTROS -->
        <div class="filters-panel">
            <div class="filters-panel__section">
                <div class="filters-panel__section-title">
                    <i class="bi bi-funnel"></i>
                    Filtros
                </div>
                <div class="filters-panel__row">
                    <div class="filters-panel__field">
                        <q-select v-model="filters.provider_id" :options="providerFilterOptions" placeholder="- Proveedor -" searchable />
                    </div>
                    <div class="filters-panel__field">
                        <q-select v-model="filters.brand_id" :options="brandFilterOptions" placeholder="- Marca -" searchable />
                    </div>
                    <div class="filters-panel__field">
                        <q-select v-model="filters.category_id" :options="categoryFilterOptions" placeholder="- Categoría -" searchable />
                    </div>
                    <div class="filters-panel__field">
                        <q-select v-model="filters.active" :options="activeOptions" />
                    </div>
                    <div class="filters-panel__field">
                        <q-select v-model="filters.qwarehouse" :options="warehouseOptions" placeholder="Todos los depositos" searchable />
                    </div>
                </div>
            </div>

            <div class="filters-panel__section">
                <div class="filters-panel__section-title">
                    <i class="bi bi-search"></i>
                    Buscadores
                </div>
                <div class="filters-panel__row">
                    <div class="filters-panel__field">
                        <input v-model="filters.qcode" class="form-control" placeholder="Buscar codigo interno...">
                    </div>
                    <div class="filters-panel__field">
                        <div class="filters-panel__scan">
                            <input v-model="filters.qbarcode" class="form-control" placeholder="Buscar codigo de barra...">
                            <barcode-scanner-button class="filters-panel__scan-btn" @scan="onBarcodeScan" />
                        </div>
                    </div>
                    <div class="filters-panel__field">
                        <input v-model="filters.qmanufacturercode" class="form-control" placeholder="Buscar codigo de fabrica...">
                    </div>
                    <div class="filters-panel__field">
                        <input v-model="filters.qname" class="form-control" placeholder="Buscar nombre...">
                    </div>
                    <div class="filters-panel__actions">
                        <button type="button" class="filters-panel__btn filters-panel__btn--outline" @click="clearFilters()">
                            <i class="bi bi-eraser"></i>
                            Limpiar todo
                        </button>
                        <button type="button" class="filters-panel__btn" @click="exportCSV()">
                            <i class="bi bi-download"></i>
                            Exportar CSV
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <!-- ACCIONES MASIVAS -->
        <div class="bulk-actions-bar" v-if="selectedIds.length > 0">
            <div class="bulk-actions-bar__count">{{ selectedIds.length }} seleccionados</div>

            <div class="bulk-actions-bar__group">
                <q-select v-model="bulkForm.category_id" :options="categoryOptions" placeholder="- Categoría -" searchable />
                <button type="button" class="filters-panel__btn" :disabled="!bulkForm.category_id" @click="bulkAssignCategory()">Asignar</button>
            </div>

            <div class="bulk-actions-bar__group">
                <q-select v-model="bulkForm.provider_id" :options="providerOptions" placeholder="- Proveedor -" searchable />
                <button type="button" class="filters-panel__btn" :disabled="!bulkForm.provider_id" @click="bulkAssignProvider()">Asignar</button>
            </div>

            <div class="bulk-actions-bar__group">
                <q-select v-model="bulkForm.brand_id" :options="brandOptions" placeholder="- Marca -" searchable />
                <button type="button" class="filters-panel__btn" :disabled="!bulkForm.brand_id" @click="bulkAssignBrand()">Asignar</button>
            </div>

            <div class="bulk-actions-bar__group">
                <span class="bulk-actions-bar__label">Estado:</span>
                <button type="button" class="filters-panel__btn filters-panel__btn--outline" @click="bulkSetActive(true)">Activar</button>
                <button type="button" class="filters-panel__btn filters-panel__btn--outline" @click="bulkSetActive(false)">Desactivar</button>
            </div>

            <button type="button" class="bulk-actions-bar__clear" @click="clearSelection()">Cancelar selección</button>
        </div>
        <!-- TABLA -->
        <div class="data-table">
            <div class="table-responsive">
                <table class="table mb-0">
                    <thead>
                        <tr>
                            <th>
                                <input type="checkbox" :checked="allSelectedOnPage" @change="toggleSelectAll($event.target.checked)" />
                            </th>
                            <th>Imagen</th>
                            <th>Codigo</th>
                            <th class="data-table__name-cell">
                                <a href="#" @click.prevent="sortList('name')" class="data-table__sort" :class="{ 'is-active': filters.sorting.col === 'name' }">
                                    Nombre
                                    <i :class="sortIcon('name')"></i>
                                </a>
                            </th>
                            <th>Categorías</th>
                            <th>Stock</th>
                            <th>
                                <a href="#" @click.prevent="sortList('price')" class="data-table__sort" :class="{ 'is-active': filters.sorting.col === 'price' }">
                                    Precio
                                    <i :class="sortIcon('price')"></i>
                                </a>
                            </th>
                            <th>
                                <a href="#" @click.prevent="sortList('update')" class="data-table__sort" :class="{ 'is-active': filters.sorting.col === 'update' }">
                                    Ultimo cambio
                                    <i :class="sortIcon('update')"></i>
                                </a>
                            </th>
                            <th>Estado</th>
                            <th class="text-right">Acción</th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr v-for="prod in productList" :key="prod.id">
                            <td>
                                <input type="checkbox" :checked="selectedIds.includes(prod.id)" @change="toggleSelectRow(prod.id)" />
                            </td>
                            <td>
                                <img
                                    class="data-table__thumb"
                                    :src="prod.image ? createurl(prod.image) : '/ph-product.png'"
                                    @mouseenter="showThumbPreview(prod, $event)"
                                    @mouseleave="hideThumbPreview()"
                                />
                            </td>
                            <td>
                                <div class="d-flex flex-column">
                                    <span class="data-table__primary" title="Codigo Interno">
                                        <i class="bi bi-hash"></i>{{prod.code}}
                                    </span>
                                    <small class="data-table__muted" title="Codigo de barra">
                                        <i class="bi bi-upc-scan mr-1" title="Codigo de barra"></i>{{prod.barcode}}
                                    </small>
                                    <small class="data-table__muted" title="Codigo de fabrica">
                                        <i class="bi bi-box-seam mr-1" title="Codigo de fabrica"></i>{{prod.manufacturer_code}}
                                    </small>
                                </div>
                            </td>
                            <td class="data-table__name-cell">
                                {{prod.name}}
                                <span v-if="prod.offer_price != null" class="badge badge-danger ml-1" title="Tiene una oferta activa">Oferta</span>
                            </td>
                            <td class="data-table__muted">
                                <span v-if="prod.categories">{{ prod.categories }}</span>
                                <span v-else class="status-badge status-badge--danger">Sin categoría</span>
                            </td>
                            <td>
                                <span v-if="prod.type === 'SERVICE'" class="status-badge status-badge--info">
                                    <i class="bi bi-tools"></i>
                                    Servicio
                                </span>
                                <span v-else class="status-badge" :class="stockBadgeClass(prod.stock)">
                                    {{prod.stock}}
                                </span>
                            </td>
                            <td @dblclick="startPriceEdit(prod)">
                                <input
                                    v-if="editingPriceId === prod.id"
                                    ref="priceInput"
                                    v-model.number="editingPriceValue"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    class="data-table__price-input"
                                    @keyup.enter="savePrice(prod)"
                                    @keyup.esc="cancelPriceEdit"
                                    @blur="savePrice(prod)"
                                />
                                <span
                                    v-else
                                    class="data-table__primary"
                                    :style="canEditPrice ? 'cursor: pointer;' : ''"
                                    :title="canEditPrice ? 'Doble click para editar el precio' : ''"
                                >{{formatoDinamicoMoneda(prod.price)}}</span>
                            </td>
                            <td>
                                <div class="d-flex flex-column">
                                    <span class="data-table__muted">{{prod.updated}}</span>
                                    <small v-if="prod.priceUpdated" class="data-table__muted" title="Último cambio de precio">
                                        <i class="bi bi-tag mr-1"></i>{{prod.priceUpdated}}
                                    </small>
                                </div>
                            </td>
                            <td>
                                <span
                                    class="status-badge"
                                    :class="prod.active == 1 ? 'status-badge--success' : 'status-badge--danger'"
                                    style="cursor: pointer;"
                                    @click="toggleProduct(prod.id)"
                                >
                                    <i :class="prod.active == 1 ? 'bi bi-check-circle-fill' : 'bi bi-dash-circle-fill'"></i>
                                    {{ prod.active == 1 ? 'Activo' : 'Inactivo' }}
                                </span>
                            </td>
                            <td class="text-right">
                                <div class="dropdown" :class="{ show: openDropdownId === prod.id }">
                                    <button
                                        class="table-action-btn"
                                        type="button"
                                        :aria-expanded="openDropdownId === prod.id ? 'true' : 'false'"
                                        @click.stop="toggleDropdown(prod.id, $event)"
                                    >
                                        <i class="bi bi-three-dots-vertical"></i>
                                    </button>
                                    <div class="dropdown-menu dropdown-menu-right" :class="{ show: openDropdownId === prod.id }" :style="openDropdownId === prod.id ? dropdownStyle : {}">
                                        <v-guard :permissions="['EDITAR_PRODUCTOS']">
                                            <a class="dropdown-item" href="#" @click.prevent="getProductDetail(prod.id); openDropdownId = null;">Editar Producto</a>
                                            <div class="dropdown-divider"></div>
                                        </v-guard>
                                        <v-guard :permissions="['EDITAR_STOCK']">
                                            <router-link class="dropdown-item" :to="{ name: 'Movimientos de stock', query: { productId: prod.id, modal: 'edit' } }" @click.native="openDropdownId = null">Editar Stock</router-link>
                                            <router-link class="dropdown-item" :to="{ name: 'Movimientos de stock', query: { productId: prod.id, modal: 'transfer' } }" @click.native="openDropdownId = null">Transferir Stock</router-link>
                                        </v-guard>
                                        <a class="dropdown-item" href="#" @click.prevent="loadPriceLists(prod.id); openDropdownId = null;">Generar etiqueta</a>
                                        <a class="dropdown-item" :href="'/inventario/' + prod.id + '/numeros-de-serie'">Numeros de serie</a>
                                        <div class="dropdown-divider"></div>
                                        <a class="dropdown-item text-danger" href="#" @click.prevent="deleteProduct(prod.id); openDropdownId = null;">Eliminar</a>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr v-if="productList.length<=0">
                            <td colspan="10" class="data-table__empty">No se encontraron productos</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- PAGINACION -->
            <div class="data-table__footer">
                <div class="data-table__info">
                    Mostrando {{ totalItems === 0 ? 0 : (filters.page - 1) * filters.itemsPerPage + 1 }}
                    a {{ Math.min(filters.page * filters.itemsPerPage, totalItems) }}
                    de {{ totalItems }} items.
                </div>

                <ul class="data-table__pagination">
                    <li class="page-item" :class="{ 'disabled': filters.page === 1 }">
                        <a class="page-link" href="#" @click.prevent="prevPage()">
                            <i class="bi bi-chevron-left"></i>
                        </a>
                    </li>
                    <li v-if="totalPages > pageInterval && (filters.page - (pageInterval / 2)) > 1" class="page-item">
                        <a class="page-link" href="#" @click.prevent="jumpPrevBlock()">...</a>
                    </li>
                    <template v-for="index in totalPages">
                        <li :key="index" v-if="inIntervalPage(index)" class="page-item" :class="{ 'active': filters.page === index }">
                            <a class="page-link" href="#" @click.prevent="changePage(index)">{{index}}</a>
                        </li>
                    </template>
                    <li v-if="totalPages > pageInterval && (filters.page + (pageInterval / 2)) < totalPages" class="page-item">
                        <a class="page-link" href="#" @click.prevent="jumpNextBlock()">...</a>
                    </li>
                    <li class="page-item" :class="{ 'disabled': filters.page >= totalPages }">
                        <a class="page-link" href="#" @click.prevent="nextPage()">
                            <i class="bi bi-chevron-right"></i>
                        </a>
                    </li>
                </ul>

                <div class="data-table__page-size">
                    <select v-model="filters.itemsPerPage" class="custom-select">
                        <option :value="15">15 por página</option>
                        <option :value="25">25 por página</option>
                        <option :value="50">50 por página</option>
                    </select>
                </div>
            </div>
        </div>

        <div
            v-if="hoveredThumb"
            class="data-table__thumb-preview"
            :style="{ top: hoveredThumb.top + 'px', left: hoveredThumb.left + 'px' }"
        >
            <img :src="hoveredThumb.url" />
        </div>
    </div>

</template>

<script>
import { mapState } from 'vuex'
import exportsMixin from "../../../mixins/exportsMixin"
import formatDateMixin from "../../../mixins/formatDateMixin";
import formatMoneyMixin from "../../../mixins/formatMoneyMixin";
import myaxios from 'axios';
import BarcodeScannerButton from '@/components/shared/BarcodeScannerButton.vue';
import QSelect from '@/components/shared/QSelect.vue';



export default {
  name: 'ProductsList',
  props: ['categoryId'],
  components: { BarcodeScannerButton, QSelect },
  mixins: [exportsMixin, formatDateMixin, formatMoneyMixin],
  data() {
    return {
      bucket: 'productimages-qupro-prod',
      region: 'sa-east-1',
      timeoutId: null,
      suppressReload: false,
      loading: false,
      productList: [],
      totalPages: 1,
      pageInterval: 16,
      totalItems: 0,
      openDropdownId: null,
      dropdownStyle: {},
      hoveredThumb: null,
      editingPriceId: null,
      editingPriceValue: null,
      savingPriceId: null,
      selectedIds: [],
      bulkForm: {
        category_id: null,
        provider_id: null,
        brand_id: null,
      },
      filters: {
        qname: '',
        qcode: '',
        qbarcode: '',
        qmanufacturercode: '',
        qwarehouse: null,
        page: 1,
        itemsPerPage: 15,
        active: 1,
        provider_id: null,
        brand_id: null,
        category_id: null,
        sorting: {
          col: '',
          order: 'desc',
        },
      },
    };
  },
  computed: {
    ...mapState('authUser', { authUser: state => state.authUser }),
    ...mapState('categories', { categories: state => state.categories }),
    ...mapState('warehouse', { warehouse: state => state.warehouse }),
    ...mapState('brands', { brands: state => state.brands }),
    ...mapState('providers', { providers: state => state.providers }),
    // La columna Precio muestra el precio de la lista configurada en la sucursal
    // (ver ProductApiController@index). Si la sucursal no tiene lista (id 0/null),
    // lo que se ve es el base_price y la edicion inline queda deshabilitada.
    pricelistId() {
      // El payload de getAuthUser trae branchoffices como array (ver Subnavbar-ventas).
      const bo = this.authUser && this.authUser.branchoffices && this.authUser.branchoffices[0];
      return bo && bo.configs ? bo.configs.pricelist_id : null;
    },
    canEditPrice() {
      return this.$permitsAny(['PRODUCTO_CAMBIAR_PRECIOS']) && this.pricelistId > 0;
    },
    allSelectedOnPage() {
      return this.productList.length > 0 && this.productList.every(p => this.selectedIds.includes(p.id));
    },
    providerOptions() {
      return (Array.isArray(this.providers) ? this.providers : []).map(p => ({ value: p.id, label: p.name }));
    },
    brandOptions() {
      return (Array.isArray(this.brands) ? this.brands : []).map(b => ({ value: b.id, label: b.name }));
    },
    categoryOptions() {
      return (Array.isArray(this.categories) ? this.categories : []).map(c => ({ value: c.id, label: c.name }));
    },
    providerFilterOptions() {
      return [{ value: 'Sin proveedor', label: 'Sin Proveedor' }, ...this.providerOptions];
    },
    brandFilterOptions() {
      return [{ value: 'Sin marca', label: 'Sin Marca' }, ...this.brandOptions];
    },
    categoryFilterOptions() {
      return [{ value: 'Sin categoria', label: 'Sin Categoría' }, ...this.categoryOptions];
    },
    activeOptions() {
      return [
        { value: 0, label: 'Todos los productos' },
        { value: 1, label: 'Productos activos' },
        { value: 2, label: 'Productos Inactivos' },
      ];
    },
    warehouseOptions() {
      return (Array.isArray(this.warehouse) ? this.warehouse : []).map(w => ({ value: w.id, label: w.name }));
    },
  },
  created() {
    this.$store.dispatch('authUser/getAuthUser');
    this.$store.dispatch('categories/getCategories');
    this.$store.dispatch('warehouse/getWarehouses');
    this.$store.dispatch('brands/getBrands');
    this.$store.dispatch('providers/getProviders');
  },
  mounted() {
    if (this.categoryId != null) {
      // Asignar el filtro dispara el watcher 'filters.category_id', que ya llama
      // a getProducts(); no lo llamamos de nuevo aca para evitar la doble peticion.
      this.filters.category_id = this.categoryId;
    } else {
      this.getProducts();
    }
  },
  // Con <keep-alive> el componente no se destruye al ir a editar un producto: se
  // "desactiva". Por eso los listeners y el refresco van en activated/deactivated.
  activated() {
    document.addEventListener('click', this.handleDocumentClick);
    window.addEventListener('scroll', this.handleScrollDismiss, true);
    // La primera activacion ocurre justo despues de mounted(), que ya cargo los
    // datos. En las siguientes (volver de editar) refrescamos para ver el cambio,
    // manteniendo los filtros, la pagina y el scroll que estaban en memoria.
    if (this.activatedBefore) {
      this.getProducts();
    }
    this.activatedBefore = true;
  },
  deactivated() {
    document.removeEventListener('click', this.handleDocumentClick);
    window.removeEventListener('scroll', this.handleScrollDismiss, true);
    this.openDropdownId = null;
    this.dropdownStyle = {};
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleDocumentClick);
    window.removeEventListener('scroll', this.handleScrollDismiss, true);
  },
  methods: {
    toggleDropdown(id, event) {
      if (this.openDropdownId === id) {
        this.openDropdownId = null;
        this.dropdownStyle = {};
        return;
      }
      this.openDropdownId = id;
      // Posiciona el menú con position:fixed para escapar del overflow:hidden de .data-table.
      if (event && event.currentTarget) {
        const rect = event.currentTarget.getBoundingClientRect();
        this.dropdownStyle = {
          position: 'fixed',
          top: (rect.bottom + 4) + 'px',
          right: (window.innerWidth - rect.right) + 'px',
          left: 'auto',
        };
      }
    },
    handleDocumentClick(e) {
      if (!e.target.closest('.data-table .dropdown')) {
        this.openDropdownId = null;
        this.dropdownStyle = {};
      }
    },
    handleScrollDismiss() {
      if (this.openDropdownId !== null) {
        this.openDropdownId = null;
        this.dropdownStyle = {};
      }
    },
    showThumbPreview(prod, event) {
      if (!prod.image) return;
      const rect = event.currentTarget.getBoundingClientRect();
      const size = 260;
      let left = rect.right + 12;
      if (left + size > window.innerWidth) left = rect.left - size - 12;
      let top = rect.top + rect.height / 2;
      if (top - size / 2 < 8) top = size / 2 + 8;
      if (top + size / 2 > window.innerHeight - 8) top = window.innerHeight - size / 2 - 8;
      this.hoveredThumb = { url: this.createurl(prod.image, false), top, left };
    },
    hideThumbPreview() {
      this.hoveredThumb = null;
    },
    createurl(img, thumb = true) {
      if (!this.authUser || !this.authUser.company_id || !img) {
        return '/ph-product.png';
      }
      const name = thumb ? img.split('.').join('_thumb.') : img;
      return 'https://' + this.bucket + '.s3.' + this.region + '.amazonaws.com/company_'
        + this.authUser.company_id + '/' + name;
    },
    getProducts() {
      this.loading = true;
      this.clearSelection();
      axios.post('/api/products/list', this.filters)
        .then(response => {
          if (response.status === 200) {
            this.productList = response.data.data.products;
            this.totalPages = response.data.data.totalPages;
            this.totalItems = response.data.data.totalItems;
            this.prioritizeMatches();
          } else {
            this.productList = [];
            this.totalPages = 0;
            this.totalItems = 0;
          }
        })
        .catch(error => console.error('getProducts:', error))
        .finally(() => { this.loading = false; });
    },
    // Reordena la pagina recibida para que las coincidencias exactas del buscador
    // queden primero, luego las que empiezan con el termino y al final las que solo
    // lo contienen. La busqueda y paginacion siguen siendo del backend; esto solo
    // reordena lo que ya vino en la pagina actual.
    prioritizeMatches() {
      // An explicit column sort must win over match ranking, otherwise this
      // client-side reorder would scramble the server-side sorted page.
      if (this.filters.sorting.col !== '') return;
      const searches = [
        { term: this.filters.qcode, field: 'code' },
        { term: this.filters.qbarcode, field: 'barcode' },
        { term: this.filters.qmanufacturercode, field: 'manufacturer_code' },
        { term: this.filters.qname, field: 'name' },
      ].filter(s => s.term && String(s.term).trim() !== '');

      if (searches.length === 0) return;

      const rank = (prod) => {
        let best = 3;
        for (const { term, field } of searches) {
          const value = String(prod[field] || '').toLowerCase();
          const t = String(term).trim().toLowerCase();
          if (value === t) best = Math.min(best, 0);
          else if (value.startsWith(t)) best = Math.min(best, 1);
          else if (value.includes(t)) best = Math.min(best, 2);
        }
        return best;
      };

      this.productList = [...this.productList].sort((a, b) => rank(a) - rank(b));
    },
    getProductsWithDelay() {
      if (this.suppressReload) return;
      clearTimeout(this.timeoutId);
      this.timeoutId = setTimeout(() => {
        this.filters.page = 1;
        this.getProducts();
      }, 1500);
    },
    reloadFromFirstPage() {
      if (this.suppressReload) return;
      this.filters.page = 1;
      this.getProducts();
    },
    // Resetea filtros y buscadores a sus valores por defecto. El flag suppressReload
    // evita que los watchers disparen una peticion por cada campo; recargamos una sola vez.
    clearFilters() {
      clearTimeout(this.timeoutId);
      this.suppressReload = true;
      this.filters.qname = '';
      this.filters.qcode = '';
      this.filters.qbarcode = '';
      this.filters.qmanufacturercode = '';
      this.filters.qwarehouse = null;
      this.filters.provider_id = null;
      this.filters.brand_id = null;
      this.filters.category_id = null;
      this.filters.active = 1;
      this.filters.page = 1;
      this.$nextTick(() => {
        this.suppressReload = false;
        this.getProducts();
      });
    },
    // Codigo escaneado con la camara (mobile): un QR lleva el codigo interno (qcode);
    // un codigo de barra 1D va a qbarcode. Carga el campo que corresponde y busca de
    // inmediato. suppressReload evita que el watcher dispare un segundo request con su debounce.
    onBarcodeScan(code, type) {
      this.suppressReload = true;
      if (type === 'qr') {
        this.filters.qcode = code;
      } else {
        this.filters.qbarcode = code;
      }
      this.$nextTick(() => {
        this.suppressReload = false;
        this.filters.page = 1;
        this.getProducts();
      });
    },
    getProductDetail(prodId) {
      this.$router.push({
        name: 'Editar producto',
        params: { productId: prodId },
      });
    },
    sortList(col) {
      if (this.filters.sorting.col === col) {
        this.filters.sorting.order = this.filters.sorting.order === 'asc' ? 'desc' : 'asc';
      } else {
        this.filters.sorting.col = col;
        this.filters.sorting.order = 'asc';
      }
      this.reloadFromFirstPage();
    },
    sortIcon(col) {
      if (this.filters.sorting.col !== col) return 'bi bi-chevron-expand';
      return this.filters.sorting.order === 'asc' ? 'bi bi-chevron-up' : 'bi bi-chevron-down';
    },
    prevPage() {
      if (this.filters.page > 1) {
        this.filters.page--;
        this.getProducts();
      }
    },
    nextPage() {
      if (this.filters.page < this.totalPages) {
        this.filters.page++;
        this.getProducts();
      }
    },
    jumpPrevBlock() {
      this.filters.page = Math.max(1, this.filters.page - this.pageInterval);
      this.getProducts();
    },
    jumpNextBlock() {
      this.filters.page = Math.min(this.totalPages, this.filters.page + this.pageInterval);
      this.getProducts();
    },
    changePage(page) {
      this.filters.page = page;
      this.getProducts();
    },
    inIntervalPage(index) {
      if (this.totalPages <= this.pageInterval) return true;

      const half = Math.floor(this.pageInterval / 2);
      let limitprev = this.filters.page - half;
      let limitnext = this.filters.page + half;

      if (limitprev < 1) {
        limitprev = 1;
        limitnext = this.pageInterval;
      }
      if (limitnext > this.totalPages) {
        limitnext = this.totalPages;
        limitprev = this.totalPages - this.pageInterval + 1;
      }

      return index >= limitprev && index <= limitnext;
    },
    toggleSelectRow(id) {
      const idx = this.selectedIds.indexOf(id);
      if (idx === -1) this.selectedIds.push(id);
      else this.selectedIds.splice(idx, 1);
    },
    toggleSelectAll(checked) {
      if (checked) this.selectedIds = this.productList.map(p => p.id);
      else this.selectedIds = [];
    },
    clearSelection() {
      this.selectedIds = [];
      this.bulkForm.category_id = null;
      this.bulkForm.provider_id = null;
      this.bulkForm.brand_id = null;
    },
    async bulkAssignCategory() {
      const ok = await this.$confirm(`¿Asignar esta categoría a ${this.selectedIds.length} productos?`, { title: 'Asignar categoría' });
      if (!ok) return;
      axios.post('/api/products/bulkAssignCategory', { product_ids: this.selectedIds, category_id: this.bulkForm.category_id })
        .then(response => {
          if (response.data.success) this.getProducts();
        })
        .catch(error => console.error('bulkAssignCategory:', error));
    },
    async bulkAssignProvider() {
      const ok = await this.$confirm(`¿Asignar este proveedor a ${this.selectedIds.length} productos?`, { title: 'Asignar proveedor' });
      if (!ok) return;
      axios.post('/api/products/bulkAssignProvider', { product_ids: this.selectedIds, provider_id: this.bulkForm.provider_id })
        .then(response => {
          if (response.data.success) this.getProducts();
        })
        .catch(error => console.error('bulkAssignProvider:', error));
    },
    async bulkAssignBrand() {
      const ok = await this.$confirm(`¿Asignar esta marca a ${this.selectedIds.length} productos? Esto reemplaza la marca actual de cada producto.`, { title: 'Asignar marca' });
      if (!ok) return;
      axios.post('/api/products/bulkAssignBrand', { product_ids: this.selectedIds, brand_id: this.bulkForm.brand_id })
        .then(response => {
          if (response.data.success) this.getProducts();
        })
        .catch(error => console.error('bulkAssignBrand:', error));
    },
    async bulkSetActive(active) {
      const label = active ? 'activar' : 'desactivar';
      const ok = await this.$confirm(`¿Desea ${label} ${this.selectedIds.length} productos?`, { title: 'Cambiar estado' });
      if (!ok) return;
      axios.post('/api/products/bulkSetActive', { product_ids: this.selectedIds, active })
        .then(response => {
          if (response.data.success) this.getProducts();
        })
        .catch(error => console.error('bulkSetActive:', error));
    },
    async toggleProduct(id) {
      const ok = await this.$confirm('¿Desea cambiar el estado del producto?', { title: 'Cambiar estado' });
      if (!ok) return;
      axios.post('/api/products/switchactive', { id })
        .then(response => {
          if (response.data.success) this.getProducts();
        })
        .catch(error => console.error('toggleProduct:', error));
    },
    async deleteProduct(id) {
      const ok = await this.$confirm(
        '¿Desea borrar este producto? Si continua se borraran tambien los movimientos de stock y sus precios asociados.',
        { title: 'Eliminar producto', okText: 'Eliminar', variant: 'danger' }
      );
      if (!ok) return;
      axios.post('/api/products/delete', { id })
        .then(response => {
          if (response.data.code === 201) this.getProducts();
        })
        .catch(error => console.error('deleteProduct:', error));
    },
    loadPriceLists(id) {
      axios.get('/api/priceslist/getByProductT/' + id)
        .then(response => {
          if (response.status === 200 && response.data.data.length > 0) {
            const selectedPrice = response.data.data[0];
            this.generateTicket(id, selectedPrice);
          }
        })
        .catch(error => console.error('loadPriceLists:', error));
    },
    generateTicket(id, selectedPrice) {
      this.loading = true;
      const outdata = {
        prod_id: id,
        withoutTax: this.formatoDinamicoMoneda(selectedPrice.withoutTax),
        price: this.formatoDinamicoMoneda(selectedPrice.amount),
      };
      axios.post('/api/products/makeTicket', outdata)
        .then(response => {
          if (response.status === 200) {
            this.getTicket(id);
          } else {
            this.loading = false;
          }
        })
        .catch(error => {
          this.loading = false;
          console.error('generateTicket:', error);
        });
    },
    getTicket(id) {
      const url = '/api/products/PDFprodticket/' + id;
      axios.get(url)
        .then(res => {
          const uninterceptedAxiosInstance = myaxios.create();
          return uninterceptedAxiosInstance.get(res.data, { responseType: 'blob' });
        })
        .then(response => {
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = 'Ticket ' + Date.now() + '.pdf';
          link.click();
          URL.revokeObjectURL(link.href);
        })
        .catch(error => console.error('getTicket:', error))
        .finally(() => { this.loading = false; });
    },
    exportCSV() {
      // El backend NO devuelve el CSV: encola el job CSVExportProduct, que genera el
      // archivo y lo deja disponible en la pantalla Descargas. Por eso no pedimos un
      // blob; solo enviamos los filtros.
      this.exportToDescargas(
        process.env.VUE_APP_BASE_URL + '/api/products/exportCSV',
        this.filters,
        'Listado de productos (CSV)'
      ).catch((error) => {
        const serverMsg = error.response && error.response.data && error.response.data.message;
        console.error('exportCSV:', serverMsg || error);
      });
    },
    nuevoProductoLink() {
      this.$router.push({ name: 'Nuevo producto' });
    },
    startPriceEdit(prod) {
      if (!this.canEditPrice || this.savingPriceId !== null) return;
      this.editingPriceId = prod.id;
      this.editingPriceValue = prod.price;
      this.$nextTick(() => {
        const ref = this.$refs.priceInput;
        const input = Array.isArray(ref) ? ref[0] : ref;
        if (input) {
          input.focus();
          input.select();
        }
      });
    },
    cancelPriceEdit() {
      this.editingPriceId = null;
      this.editingPriceValue = null;
    },
    // Guarda via view + edit (el mismo camino que usa el Edit-modal) en vez de
    // /api/products/addPrice: addPrice borra y recrea la fila de product_prices,
    // lo que pisa utility y rompe la actualizacion automatica de precios al
    // registrar compras con "actualizar costo".
    async savePrice(prod) {
      // El guard corta el blur que dispara el propio save (enter/esc ya limpiaron el estado).
      if (this.editingPriceId !== prod.id) return;
      // Input vacio = edicion abandonada, no "precio 0" (Number('') === 0).
      if (this.editingPriceValue === '' || this.editingPriceValue === null) {
        this.cancelPriceEdit();
        return;
      }
      const newPrice = Number(this.editingPriceValue);
      this.cancelPriceEdit();
      if (!isFinite(newPrice) || newPrice < 0 || newPrice === Number(prod.price)) return;
      const msg = '¿Cambiar el precio de "' + prod.name + '" de '
        + this.formatoDinamicoMoneda(prod.price) + ' a ' + this.formatoDinamicoMoneda(newPrice) + '?';
      const ok = await this.$confirm(msg, { title: 'Cambiar precio' });
      if (!ok) return;
      this.savingPriceId = prod.id;
      axios.get('/api/products/view/' + prod.id)
        .then(response => {
          const p = response.data.data;
          const prices = Object.assign({}, p.prices);
          prices[this.pricelistId] = newPrice;
          // Mismo payload que arma el Edit-modal en setForEdit/updateProduct: el update
          // del backend sincroniza categorias, proveedores, variantes e imagenes, asi
          // que hay que reenviar el estado actual completo o se pierden asociaciones.
          const payload = {
            id: p.id,
            name: p.name,
            code: p.code,
            barcode: p.barcode,
            dispatch_code: p.dispatch_code,
            stock_critical: p.stock_critical,
            stock_minimum: p.stock_minimum,
            stock_initial: 0,
            unit: p.unit,
            base_price: p.base_price,
            iva: p.iva,
            prov_linked: p.prov_linked,
            type: p.type,
            brand_id: p.brand_id,
            prices,
            ecom_active: p.ecom_active,
            description: p.description,
            manufacturer_code: p.manufacturer_code,
            currency: p.currency,
            exchange_rate: p.exchange_rate,
            cat_linked: p.cat_linked,
            var_linked: p.var_linked,
            vars_new: [],
            images: p.images || [],
            setimagesid: (p.images || []).map(e => e.id),
            atts_linked: p.attribute_types || [],
            editable: p.editable,
            priceUpd: p.priceUpd,
          };
          return axios.post('/api/products/edit/' + prod.id, payload);
        })
        .then(response => {
          if (response && response.data && response.data.code === 201) {
            // Refresca la pagina actual para mostrar precio y "ultimo cambio de
            // precio" recalculados por el backend.
            this.getProducts();
          }
        })
        .catch(error => console.error('savePrice:', error))
        .finally(() => { this.savingPriceId = null; });
    },
    stockBadgeClass(stock) {
      const n = parseFloat(stock);
      if (isNaN(n) || n === 0) return 'status-badge--neutral';
      // Negative stock means overselling: keep it as an alert.
      if (n < 0) return 'status-badge--danger';
      if (n < 5) return 'status-badge--warning';
      return 'status-badge--success';
    },
  },
  watch: {
    'filters.itemsPerPage'() { this.reloadFromFirstPage(); },
    'filters.active'() { this.reloadFromFirstPage(); },
    'filters.qwarehouse'() { this.reloadFromFirstPage(); },
    'filters.category_id'() { this.reloadFromFirstPage(); },
    'filters.brand_id'() { this.reloadFromFirstPage(); },
    'filters.provider_id'() { this.reloadFromFirstPage(); },
    'filters.qname'() { this.getProductsWithDelay(); },
    'filters.qcode'() { this.getProductsWithDelay(); },
    'filters.qbarcode'() { this.getProductsWithDelay(); },
    'filters.qmanufacturercode'() { this.getProductsWithDelay(); },
  },
};
</script>

